import "date-fns";
import React, { useEffect, useState } from "react";
import WizardButtons from './WizardButtons';
import { useForm, useFormState } from "react-hook-form";
import DateFnsUtils from "@date-io/date-fns";
import {
    Grid,
    FormControl,
    TextField,
    Button,
    Tooltip,
    Typography,
    IconButton,
    createMuiTheme,
    ThemeProvider
} from "@material-ui/core";
import { CloudDownload } from "@material-ui/icons";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker
} from "@material-ui/pickers";
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { setHospitalisedForm } from "../../application/actions";
import { useAppState } from "../../application/AppState";
import useTranslation from "../../application/translation";
import { InputFiles } from "../../application/helpers/InputFiles";
import { getDefaultValue2 } from "../../application/helpers/form";
import { claimTypeEnum } from "../../application/enum";

import { validString } from "../../application/validation";

export default function FormHospitalised({ handleBack, handleNext }) {
    const [{ formValues, claimDate }, dispatch] = useAppState();
    const translation = useTranslation();

    const datePickerTheme = createMuiTheme({
        overrides: {
            MuiPickersCalendarHeader: {
                dayLabel: {
                    color: '#000000',
                    fontWeight: 600,
                }
            }
        }
    });

    const formType = FormHospitalised.name;
    const initClaimType = getDefaultValue2(formValues, formType, "typeSelector") ?? claimTypeEnum.Accident;
    const [claimType, setClaimType] = useState(initClaimType);

    const errorObj = {
        required_error: translation.error_mandatory,
        invalid_type_error: translation.error_invalidentry,
    };
    const textRequired = { message: translation.error_mandatory };
    const invalidEntry = { message: translation.error_invalidentry}

    const accidentSchema = z.object({
        typeSelector: z.literal(claimTypeEnum.Accident),
        conditionStartDate: z.date(errorObj),
        accidentLocation: z.string().min(1, textRequired).refine(validString, invalidEntry),
        description: z.string().min(1, textRequired).refine(validString, invalidEntry),
        doctorHiredDate: z.date(errorObj),
        doctorNameEmail: z.string().min(1, textRequired).refine(validString, invalidEntry),
    }).passthrough();

    const diseaseSchema = z.object({
        typeSelector: z.literal(claimTypeEnum.Disease),
        conditionStartDate: z.date(errorObj),
        description: z.string().min(1, textRequired).refine(validString, invalidEntry),
        doctorHiredDate: z.date(errorObj),
        doctorNameEmail: z.string().min(1, textRequired).refine(validString, invalidEntry),
    }).passthrough();

    const formSchema = z.union([accidentSchema, diseaseSchema]);

    const { handleSubmit, register, setValue, getValues, control, reset } = useForm({
        mode: "onChange",
        defaultValues: {
            typeSelector: initClaimType,
            conditionStartDate: getDefaultValue2(formValues, formType, "conditionStartDate"),
            accidentLocation: getDefaultValue2(formValues, formType, "accidentLocation"),
            firstDayInHospitalDate: claimDate,
            lastDayInHospitalDate: getDefaultValue2(formValues, formType, "lastDayInHospitalDate"),
            description: getDefaultValue2(formValues, formType, "description"),
            doctorHiredDate: getDefaultValue2(formValues, formType, "doctorHiredDate"),
            doctorNameEmail: getDefaultValue2(formValues, formType, "doctorNameEmail"),
            medicalCertFile: getDefaultValue2(formValues, formType, "medicalCertFile"),
            medicalJournalFile: getDefaultValue2(formValues, formType, "medicalJournalFile"),
            terminationCertFile: getDefaultValue2(formValues, formType, "terminationCertFile"),
            unemploymentCertFile: getDefaultValue2(formValues, formType, "unemploymentCertFile"),
        },
        resolver: zodResolver(formSchema),
    });
    const { errors } = useFormState({ control });

    // useEffect(() => {
    //     reset();
    // }, [reset]);

    const onSubmit = data => {
        dispatch(setHospitalisedForm(data));
        handleNext();
    };

    const handleClaimTypeChange = e => setClaimType(e.target.value);

    const handleDateByName = (date, name) => {
        setValue(name, date, { shouldValidate: true });
    };

    const values = getValues();

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <ThemeProvider theme={datePickerTheme}>
                    <Grid container spacing={3}>

                        {/* --------------- Claim type (native select) --------------- */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <TextField
                                    select
                                    variant="outlined"
                                    SelectProps={{
                                        native: true,
                                        inputProps: {
                                            'aria-label': translation.hospital_type
                                        }
                                    }}
                                    id="typeSelector"
                                    label={translation.hospital_type}
                                    value={claimType}
                                    {...register("typeSelector")}
                                    onChange={handleClaimTypeChange}
                                >
                                    <option value={claimTypeEnum.Accident}>
                                        {translation.hospital_accident}
                                    </option>
                                    <option value={claimTypeEnum.Disease}>
                                        {translation.hospital_sick}
                                    </option>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} />

                        {/* --------------- Condition start --------------- */}
                        <Grid item xs={12} sm={6}>
                            <KeyboardDatePicker
                                id="conditionStartDate"
                                label={
                                    claimType === claimTypeEnum.Disease
                                        ? translation.sick_sickwhen
                                        : translation.hospital_accidentdate
                                }
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={values.conditionStartDate || null}
                                onChange={d => handleDateByName(d, "conditionStartDate")}
                                disableFuture
                                error={!!errors.conditionStartDate}
                                helperText={errors.conditionStartDate?.message}
                                KeyboardButtonProps={{
                                'aria-label': translation.open_calendar,
                                title: translation.open_calendar
                                }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* --------------- Accident location (conditional) --------------- */}
                        <Grid item xs={12} sm={6}>
                            {claimType === claimTypeEnum.Accident && (
                                <TextField
                                    id="accidentLocation"
                                    label={translation.hospital_accidentlocation}
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.accidentLocation}
                                    helperText={errors.accidentLocation?.message}
                                    {...register("accidentLocation")}
                                />
                            )}
                        </Grid>

                        {/* --------------- First hospital day (read-only) --------------- */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="firstDayInHospitalDate"
                                label={translation.hospital_firsthospitalday}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={claimDate}
                                disabled
                                KeyboardButtonProps={{
                                    'aria-label': translation.open_calendar
                                }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* --------------- Last hospital day --------------- */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="lastDayInHospitalDate"
                                label={translation.hospital_lasthospitalday}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={values.lastDayInHospitalDate || null}
                                onChange={d => handleDateByName(d, "lastDayInHospitalDate")}
                                error={!!errors.lastDayInHospitalDate}
                                helperText={errors.lastDayInHospitalDate?.message}
                                KeyboardButtonProps={{
                                    'aria-label': translation.open_calendar
                                }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* --------------- Description --------------- */}
                        <Grid item xs={12}>
                            <TextField
                                id="description"
                                label={
                                    claimType === claimTypeEnum.Disease
                                        ? translation.hospital_symptoms
                                        : translation.hospital_accidenthow
                                }
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={3}
                                margin="normal"
                                error={!!errors.description}
                                helperText={errors.description?.message}
                                {...register("description")}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* --------------- Doctor notice date --------------- */}
                        <Grid item xs={12}>
                            <KeyboardDatePicker
                                id="doctorHiredDate"
                                label={translation.hospital_doctordate}
                                format={translation.dateformat}
                                margin="normal"
                                inputVariant="outlined"
                                value={values.doctorHiredDate || null}
                                onChange={d => handleDateByName(d, "doctorHiredDate")}
                                error={!!errors.doctorHiredDate}
                                helperText={errors.doctorHiredDate?.message}
                                KeyboardButtonProps={{
                                    'aria-label': translation.open_calendar
                                }}
                                leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                                rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            />
                        </Grid>

                        {/* --------------- Doctor name & email --------------- */}
                        <Grid item xs={12}>
                            <TextField
                                id="doctorNameEmail"
                                label={translation.hospital_doctornameadress}
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={5}
                                margin="normal"
                                error={!!errors.doctorNameEmail}
                                helperText={errors.doctorNameEmail?.message}
                                {...register("doctorNameEmail")}
                            />
                        </Grid>

                        {/* --------------- Attachments tooltip --------------- */}
                        <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center" role="group" aria-labelledby="fileAttachmentsHeading">
                                <Grid item>
                                    <Typography id="fileAttachmentsHeading" variant="subtitle1">
                                        {translation.file_attatchmentsInfo}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Tooltip
                                        title={
                                            <span>
                                                <strong>{translation.file_tooltip1}</strong><br /><br />
                                                {translation.file_tooltip2}<br /><br />
                                                {translation.file_tooltip3}
                                            </span>
                                        }
                                    >
                                        <IconButton aria-label={translation.file_tooltip1}>
                                            <HelpOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Typography variant="body2">
                                {translation.file_attachmentsInfo2}
                            </Typography>
                        </Grid>

                        {/* --------------- File uploads --------------- */}
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.hospital_file_doctorcert}</Typography>
                            <InputFiles fieldName="medicalCertFile" errors={errors} register={register} formType={formType} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.hospital_file_journals}</Typography>
                            <InputFiles fieldName="medicalJournalFile" errors={errors} register={register} formType={formType} />
                        </Grid>

                        {/* --------------- Download link --------------- */}
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.sick_doctorsatt_info}</Typography>
                            <Button
                                variant="text"
                                startIcon={<CloudDownload color="primary" />}
                                target="_blank"
                                href={`${process.env.PUBLIC_URL}/Documents/${translation.doctorAttFile}`}
                            >
                                {translation.sick_doctorslink}
                            </Button>
                        </Grid>
                    </Grid>
                </ThemeProvider>
            </MuiPickersUtilsProvider>
            <WizardButtons handleBack={handleBack} isStepValid={true} />
        </form>
    );
}
